import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Container, Card } from 'react-bootstrap';

const MethodologyDiagram = () => {
  const svgRef = useRef(null);

  useEffect(() => {
    drawDiagram();
  }, []);

  const drawDiagram = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const width = 1200;
    const height = 800;
    const margin = { top: 20, right: 20, bottom: 20, left: 20 };

    svg
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const stages = [
      {
        id: 'data-collection',
        title: 'Data Collection',
        x: 100,
        y: 100,
        width: 200,
        height: 120,
        color: '#4A90E2',
        details: [
          'M&A transaction records',
          'Company founding dates',
          'Business models (SW/HW)',
          'Technology impact scores'
        ]
      },
      {
        id: 'preprocessing',
        title: 'Data Preprocessing',
        x: 400,
        y: 100,
        width: 200,
        height: 120,
        color: '#7B68EE',
        details: [
          'Date normalization',
          'Lifetime calculation',
          'Business model encoding',
          'Missing value handling'
        ]
      },
      {
        id: 'feature-engineering',
        title: 'Feature Engineering',
        x: 700,
        y: 100,
        width: 200,
        height: 120,
        color: '#FF6B6B',
        details: [
          'Temporal features',
          'Technology indicators',
          'Business model mapping',
          '3D coordinate space'
        ]
      },
      {
        id: 'clustering',
        title: 'Clustering Methods',
        x: 250,
        y: 300,
        width: 500,
        height: 180,
        color: '#4ECDC4',
        details: []
      },
      {
        id: 'validation',
        title: 'Validation & Metrics',
        x: 100,
        y: 550,
        width: 200,
        height: 120,
        color: '#45B7D1',
        details: [
          'Silhouette Score',
          'Davies-Bouldin Index',
          'Calinski-Harabasz Score',
          'Parameter justification'
        ]
      },
      {
        id: 'interpretation',
        title: 'Interpretation',
        x: 400,
        y: 550,
        width: 200,
        height: 120,
        color: '#F7DC6F',
        details: [
          'Cluster characteristics',
          'M&A wave identification',
          'Technology era mapping',
          'Business insights'
        ]
      },
      {
        id: 'visualization',
        title: 'Visualization',
        x: 700,
        y: 550,
        width: 200,
        height: 120,
        color: '#BB8FCE',
        details: [
          'Interactive charts',
          'Timeline analysis',
          'Network graphs',
          'Statistical dashboards'
        ]
      }
    ];

    const clusteringMethods = [
      {
        id: 'kmeans',
        parent: 'clustering',
        title: 'K-Means',
        x: 280,
        y: 340,
        width: 180,
        height: 100,
        color: '#95E1D3',
        details: [
          'k = 4 clusters',
          'Centroid-based',
          'Euclidean distance'
        ]
      },
      {
        id: 'dbscan',
        parent: 'clustering',
        title: 'DBSCAN',
        x: 520,
        y: 340,
        width: 180,
        height: 100,
        color: '#F3A683',
        details: [
          'eps = 365 days',
          'minPts = 5',
          'Density-based'
        ]
      }
    ];

    const connections = [
      { from: 'data-collection', to: 'preprocessing' },
      { from: 'preprocessing', to: 'feature-engineering' },
      { from: 'feature-engineering', to: 'clustering' },
      { from: 'clustering', to: 'validation' },
      { from: 'clustering', to: 'interpretation' },
      { from: 'interpretation', to: 'visualization' },
      { from: 'validation', to: 'interpretation' }
    ];

    const defs = svg.append("defs");

    defs.append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 5)
      .attr("refY", 0)
      .attr("markerWidth", 5)
      .attr("markerHeight", 5)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#333");

    connections.forEach(conn => {
      const fromStage = stages.find(s => s.id === conn.from);
      const toStage = stages.find(s => s.id === conn.to);

      if (fromStage && toStage) {
        const fromX = fromStage.x + fromStage.width / 2;
        const fromY = fromStage.y + fromStage.height;
        const toX = toStage.x + toStage.width / 2;
        const toY = toStage.y;

        g.append("path")
          .attr("d", `M${fromX},${fromY} L${toX},${toY}`)
          .attr("stroke", "#666")
          .attr("stroke-width", 2)
          .attr("fill", "none")
          .attr("marker-end", "url(#arrowhead)")
          .attr("opacity", 0.7);
      }
    });

    const stageGroups = g.selectAll(".stage")
      .data(stages)
      .enter()
      .append("g")
      .attr("class", "stage");

    stageGroups.append("rect")
      .attr("x", d => d.x)
      .attr("y", d => d.y)
      .attr("width", d => d.width)
      .attr("height", d => d.height)
      .attr("fill", d => d.color)
      .attr("stroke", "#333")
      .attr("stroke-width", 2)
      .attr("rx", 10)
      .attr("opacity", 0.9);

    stageGroups.append("text")
      .attr("x", d => d.x + d.width / 2)
      .attr("y", d => d.y + 25)
      .attr("text-anchor", "middle")
      .attr("font-weight", "bold")
      .attr("font-size", "14px")
      .attr("fill", "white")
      .text(d => d.title);

    stageGroups.each(function(stage) {
      const group = d3.select(this);
      stage.details.forEach((detail, i) => {
        group.append("text")
          .attr("x", stage.x + stage.width / 2)
          .attr("y", stage.y + 45 + (i * 18))
          .attr("text-anchor", "middle")
          .attr("font-size", "11px")
          .attr("fill", "white")
          .text(`• ${detail}`);
      });
    });

    const methodGroups = g.selectAll(".method")
      .data(clusteringMethods)
      .enter()
      .append("g")
      .attr("class", "method");

    methodGroups.append("rect")
      .attr("x", d => d.x)
      .attr("y", d => d.y)
      .attr("width", d => d.width)
      .attr("height", d => d.height)
      .attr("fill", d => d.color)
      .attr("stroke", "#333")
      .attr("stroke-width", 1.5)
      .attr("rx", 8)
      .attr("opacity", 0.95);

    methodGroups.append("text")
      .attr("x", d => d.x + d.width / 2)
      .attr("y", d => d.y + 20)
      .attr("text-anchor", "middle")
      .attr("font-weight", "bold")
      .attr("font-size", "13px")
      .attr("fill", "#333")
      .text(d => d.title);

    methodGroups.each(function(method) {
      const group = d3.select(this);
      method.details.forEach((detail, i) => {
        group.append("text")
          .attr("x", method.x + method.width / 2)
          .attr("y", method.y + 40 + (i * 16))
          .attr("text-anchor", "middle")
          .attr("font-size", "11px")
          .attr("fill", "#333")
          .text(detail);
      });
    });

    g.append("text")
      .attr("x", width / 2 - margin.left)
      .attr("y", 30)
      .attr("text-anchor", "middle")
      .attr("font-size", "24px")
      .attr("font-weight", "bold")
      .attr("fill", "#333")
      .text("M&A Clustering Analysis Methodology");

    const legendData = [
      { label: 'Data Flow', color: '#666' },
      { label: 'Processing Stage', color: '#4A90E2' },
      { label: 'Analysis Method', color: '#4ECDC4' }
    ];

    const legend = g.append("g")
      .attr("transform", `translate(${width - 250}, 20)`);

    legendData.forEach((item, i) => {
      const legendItem = legend.append("g")
        .attr("transform", `translate(0, ${i * 25})`);

      if (item.label === 'Data Flow') {
        legendItem.append("line")
          .attr("x1", 0)
          .attr("y1", 10)
          .attr("x2", 30)
          .attr("y2", 10)
          .attr("stroke", item.color)
          .attr("stroke-width", 2)
          .attr("marker-end", "url(#arrowhead)");
      } else {
        legendItem.append("rect")
          .attr("width", 20)
          .attr("height", 20)
          .attr("fill", item.color)
          .attr("opacity", 0.9)
          .attr("rx", 4);
      }

      legendItem.append("text")
        .attr("x", 35)
        .attr("y", 15)
        .attr("font-size", "12px")
        .attr("fill", "#333")
        .text(item.label);
    });
  };

  return (
    <Container fluid className="p-4">
      <Card>
        <Card.Header>
          <h3>Research Methodology Overview</h3>
        </Card.Header>
        <Card.Body>
          <div className="mb-3">
            <p>
              This diagram illustrates the complete methodology pipeline for analyzing merger and acquisition patterns
              in the Electronic Design Automation industry. The process flows from data collection through multiple
              stages of preprocessing, feature engineering, and clustering analysis, culminating in validated insights
              and visualizations.
            </p>
            <p>
              <strong>Key methodological innovations:</strong>
            </p>
            <ul>
              <li>Dual clustering approach combining K-Means and DBSCAN for comprehensive pattern detection</li>
              <li>Parameter optimization using multiple validation metrics (Silhouette, Davies-Bouldin, Calinski-Harabasz)</li>
              <li>Technology impact scoring aligned with historical computing evolution phases</li>
              <li>3D feature space incorporating temporal, business model, and company lifetime dimensions</li>
            </ul>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <svg ref={svgRef}></svg>
          </div>
          <div className="mt-3">
            <h5>Parameter Justification</h5>
            <p>
              <strong>DBSCAN Parameters:</strong> eps=365 days captures annual M&A cycles; minPts=5 ensures statistical significance
            </p>
            <p>
              <strong>K-Means Parameters:</strong> k=4 determined through elbow method analysis, representing distinct M&A wave periods
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MethodologyDiagram;